/**
 * GapAddButton — "+" button that appears in the CSS grid gaps between widgets.
 *
 * Renders an invisible hover zone positioned over the gap (vertical or horizontal).
 * On hover, a small blue "+" circle appears. Clicking it opens a dropdown to
 * select a widget type. On selection, the `onSelect(widgetBaseConf)` callback fires.
 *
 * Props:
 *   - position: "right" (vertical gap between widgets in same row)
 *               or "top" (horizontal gap between rows)
 *   - onSelect: (widgetBaseConf) => void — called when user picks a widget type
 *   - gridGap: the CSS grid gap in px, used to size/position the hover zone
 */

import React, { useEffect, useRef, useState } from "react";
import { colors2, icons, List } from "oolib";
import { useAddableWidgetsBaseConfs } from "../../CONFIG";

const { Plus } = icons;

export const GapAddButton = ({ position, onSelect, gridGap }) => {
  const addableWidgetsBaseConfs = useAddableWidgetsBaseConfs();
  const [hovered, setHovered] = useState(false);
  const [showList, setShowList] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!showList) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowList(false);
        setHovered(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showList]);

  const isVertical = position === "right";

  // The hover zone covers the grid gap area adjacent to the widget edge.
  // For "right": a tall thin strip to the right of the widget (in the vertical gap)
  // For "top": a wide thin strip above the widget (in the horizontal gap)
  const hoverZoneStyle = isVertical
    ? { left: "100%", top: 0, bottom: 0, width: gridGap }
    : { bottom: "100%", left: 0, right: 0, height: gridGap };

  return (
    <div
      ref={wrapperRef}
      data-dnd-hide
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!showList) setHovered(false); }}
      style={{
        position: "absolute",
        ...hoverZoneStyle,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 20,
      }}
    >
      {(hovered || showList) && (
        <div style={{ position: "relative" }}>
          {/* The "+" button — white backing div prevents grey10 transparency bleed */}
          <div style={{ borderRadius: 8, background: colors2.white }}>
            <div
              onClick={() => setShowList(prev => !prev)}
              style={{
                width: 20,
                height: 20,
                borderRadius: 8,
                background: colors2.grey10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Plus size={10} weight="bold" color={colors2.grey60} />
            </div>
          </div>

          {/* Widget type selection dropdown */}
          {showList && (
            <div
              style={{
                position: "absolute",
                ...(isVertical
                  ? { left: "100%", top: "50%", transform: "translateY(-50%)", marginLeft: 4 }
                  : { top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 4 }
                ),
                zIndex: 999,
                minWidth: 175,
              }}
            >
              <List
                options={addableWidgetsBaseConfs}
                onChange={(id, conf) => {
                  onSelect(conf);
                  setShowList(false);
                  setHovered(false);
                }}
                style={{ textWrap: "nowrap" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
